/* eslint-disable react/jsx-no-bind */
import React, { useState } from 'react'
import { t } from 'ttag'

import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'

import { show } from 'redux-modal'
import styled from 'styled-components/macro'
import { updateSelectedFilters } from 'store/actions/filters'
import Button from 'components/ui/Button'
import FilterTitle from 'components/ui/FilterTitle'
import SearchFilter from 'components/ui/SearchFilter'
import SelectedFilter from 'components/ui/SelectedFilter'
import FilterText from 'components/ui/FilterText'
import RemoveIcon from 'components/ui/RemoveIcon'
import SelectedFilterContainer from 'components/ui/SelectedFilterContainer'
import CustomRadioButton from 'components/ui/CustomRadioButton'
import { SELECTED_FILTER_MAX_LINES } from 'store/constants/constants'
import Dotdotdot from 'react-dotdotdot'
import CustomDropDownTreeSelect from 'components/ui/CustomDropdownTreeSelect'

const SubHeader = styled.h5`
  font-size: 12px;
  margin-bottom: 10px;
  margin-top: 10px;
  font-family: 'Open Sans';
  color: #4a4a4a;
`
const StyledCustomRadioButton = styled(CustomRadioButton)`
  display: inline-block;
  margin-right: 20px;
`
const Input = styled.input`
  margin-right: 2px;
`

const Label = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: #4a4a4a !important;
  padding-left: 24px !important;
`

function Themes({ type, filterData, values, title, onClose, showFilter, keyWord, customTop }) {
  const filters = useSelector(state => state.filters)
  const { currentSearchable } = filters
  const filterObject = filters.selectedFilters[currentSearchable][type]
  return (
    <PureThemes
      type={type}
      values={values}
      filterData={filterData}
      title={title}
      onClose={onClose}
      showFilter={showFilter}
      keyWord={keyWord}
      customTop={customTop}
      filterObject={filterObject}
    />
  )
}

const PureThemes = React.memo(function PureThemes({
  type,
  filterData,
  values,
  title,
  onClose,
  showFilter,
  keyWord,
  customTop,
  filterObject
}) {
  const dispatch = useDispatch()
  const selectedSearchType = filterObject[values[0]].type
  const selectedValues = filterObject[values[0]].list
  const [disabled, setDisabled] = useState(false)
  const options = filterData.map(option => {
    const parentOption = {
      label: option[keyWord],
      value: option.id,
      expanded: true,
      disabled
    }
    const selectedOption = selectedValues.filter(
      s => s.id === option.id && s.label === option[keyWord]
    )
    if (selectedOption.length > 0) {
      parentOption.checked = true
      parentOption.disabled = false
    }
    return parentOption
  })

  function sendData(list, searchType) {
    const returnValue = {
      list,
      type: searchType
    }
    const data = {
      filterType: type,
      filterId: values[0],
      filterValue: returnValue
    }
    dispatch(updateSelectedFilters(data))
  }

  const onChange = (currentNode, selectedNodes) => {
    const filterValue = selectedNodes.map(node => ({
      id: node.value,
      label: node.label
    }))
    if (selectedSearchType === 'and') {
      if (selectedNodes.length < selectedValues.length) {
        if (selectedNodes.length > 3) {
          setDisabled(true)
        } else {
          setDisabled(false)
        }
        sendData(filterValue, selectedSearchType)
      } else if (selectedNodes.length > 3) {
        setDisabled(true)
        dispatch(
          show('alertModal', {
            title,
            message: t`When using the AND modifier you can select only three themes.`
          })
        )
      } else {
        sendData(filterValue, selectedSearchType)
      }
    } else {
      setDisabled(false)
      sendData(filterValue, selectedSearchType)
    }
  }

  function handleOptionChange(changeEvent) {
    if (selectedValues.length > 3 && changeEvent.target.value === 'and') {
      setDisabled(true)
      dispatch(
        show('alertModal', {
          title,
          message: t`You have more than three themes selected. When using the AND modifier there won't be any search results with more than three themes selected.`
        })
      )
    } else {
      setDisabled(false)
    }
    const returnValue = {
      list: selectedValues,
      type: changeEvent.target.value
    }
    const data = {
      filterType: type,
      filterId: values[0],
      filterValue: returnValue
    }
    dispatch(updateSelectedFilters(data))
  }

  function handleFilterRemove(filterId, filterValue, valueIsArray = false) {
    let { list } = filterObject[values[0]]
    let searchType = filterObject[values[0]].type
    if (valueIsArray) {
      list = filterObject[values[0]][filterId].filter(
        s => s.id !== filterValue.id || s !== filterValue
      )
      if (
        selectedSearchType === 'or' ||
        (selectedValues.length <= 4 && selectedSearchType === 'and')
      ) {
        setDisabled(false)
      }
    } else {
      searchType = null
    }
    const resultingValue = {
      list,
      type: searchType
    }
    const data = {
      filterType: type,
      filterId: values[0],
      filterValue: resultingValue
    }
    dispatch(updateSelectedFilters(data))
  }

  function renderSelectedValues() {
    return ['list'].map(value => {
      if (filterObject[values[0]][value]) {
        const filterValue = filterObject[values[0]][value]
        if (filterValue instanceof Array) {
          return filterValue.map(v => (
            <SelectedFilterContainer className="selected-filters" key={v.id ? v.id : v}>
              <SelectedFilter>
                <FilterText title={v.label ? v.label : v}>
                  <Dotdotdot clamp={SELECTED_FILTER_MAX_LINES}>{v.label ? v.label : v}</Dotdotdot>
                </FilterText>
                <RemoveIcon
                  className="far fa-times"
                  onClick={() => handleFilterRemove(value, v, true)}
                />
              </SelectedFilter>
            </SelectedFilterContainer>
          ))
        }
      }
      return false
    })
  }

  return (
    <>
      <SearchFilter open={showFilter} customTop={customTop}>
        <FilterTitle>{title}</FilterTitle>
        <CustomDropDownTreeSelect data={options} onChange={onChange} />
        <div>
          <SubHeader>{t`Select search operator`}</SubHeader>
          <StyledCustomRadioButton>
            <Input
              type="radio"
              name="and_option"
              id="and_option"
              value="and"
              checked={selectedSearchType === 'and'}
              onChange={handleOptionChange}
            />
            <Label htmlFor="and_option">AND</Label>
          </StyledCustomRadioButton>
          <StyledCustomRadioButton>
            <Input
              type="radio"
              name="or_option"
              id="or_option"
              value="or"
              checked={selectedSearchType === 'or'}
              onChange={handleOptionChange}
            />
            <Label htmlFor="or_option">OR</Label>
          </StyledCustomRadioButton>
        </div>
        <Button size="medium" onClick={onClose}>
          {t`Close`}
        </Button>
      </SearchFilter>
      <div>{renderSelectedValues()}</div>
    </>
  )
})

Themes.propTypes = {
  type: PropTypes.string,
  filterData: PropTypes.array,
  values: PropTypes.array,
  title: PropTypes.string,
  onClose: PropTypes.func,
  showFilter: PropTypes.bool,
  keyWord: PropTypes.string,
  customTop: PropTypes.string
}

PureThemes.propTypes = {
  type: PropTypes.string,
  filterData: PropTypes.array,
  values: PropTypes.array,
  title: PropTypes.string,
  onClose: PropTypes.func,
  showFilter: PropTypes.bool,
  keyWord: PropTypes.string,
  customTop: PropTypes.string,
  filterObject: PropTypes.object
}

export default Themes
